import { cloneDeep, floor, isEmpty } from "lodash";
import {
    FetchState,
    FetchStateKey,
    FundingOption,
    HsaTotalState,
    PayPeriodUpdateResultCode,
    PayrollSummary,
    PayrollSummaryPersonalScheduledContributions,
    PayrollSummaryWithPriorEmployer,
} from "@/shared/types";
import { fetchStateKeyMatch } from "@/shared/fetchStateHelper";
import * as storeHelper from "@/shared/storeHelper";
import * as payrollService from "@/shared/api/payrollService";
import moment from "moment";
import { AxiosResponse } from "axios";
import { errorIsStatus } from "@/shared/errorHelper";
import { defineStore } from "pinia";
import { useUserStore } from "@/shared/store/user";
import { useNotifierStore } from "@/shared/store/notifier";
import { useHsaLimitStore } from "@/shared/store/hsalimit";
import { useHsaStore } from "@/shared/store/hsa";
import { useHsaDailyBalanceStore } from "@/shared/store/hsaDailyBalance";
import * as UserService from "@/shared/api/userService";

function mappedUserPayrollSummary (store, username: string, partnerId: number):
    Promise<AxiosResponse<PayrollSummaryWithPriorEmployer>> {
    payrollService.getUserPayrollSummaryPersonalScheduledContributions()
        .then(response => store.setPayrollSummaryPersonalScheduledContributions(response.data));
    return payrollService
        .getUserPayrollSummary(username, partnerId)
        .then(originalResponse => {
            const srcData = originalResponse.data;
            const data: PayrollSummary = {
                ...srcData,
                employerAmount: srcData.employerAmount + srcData.otherEmployerAmount,
                totalPastRecurring: srcData.totalPastRecurring + srcData.otherEmployeeAmount
            };

            return originalResponse;
        });
}


const emptyState: HsaTotalState = {
    userPayrollFetchState: FetchState.Uninitialized,
    payrollSummary: {
        hsaLimit: 0,
        employerAmount: 0,
        nextEligiblePayDate: null,
        nextPayDate: null,
        nextEligiblePayPeriodAmount: 0,
        eligibilityStatus: null,
        remainingPayPeriods: 0,
        oneTimeLift: {
            payDate: null,
            amount: 0,
            isOneTimeLiftLocked: null
        },
        nextPayrollFunding: {
            payrollDate: null,
            employerAmount: null,
            employeeRecurringAmount: null,
            employeeOneTimeAmount: null
        },
        totalOneTimeAmount: 0,
        totalPastRecurring: 0,
        totalUpcomingRecurring: 0,
        totalUpcomingOneTime: 0,
        nextYearEmployerTotal: 0,
        nextYearEmployeeTotal: 0,
        hsaIrsLimitsByYear: {},
        personalScheduledContributionsByYear: {}
    },
    earlyDepositCompleteDate: null,
    showProjectedVsActualLinks: true,
    nextYear: null,
    allowPayrollContributions: true,
    alwaysUseInitialAnnualContributionForTotalEmployeePayrollContributions: false,
    fetchKey: null,
    otherHsaAmountsByYear: {},
    otherHsaAmountsFetchState: FetchState.Uninitialized,
    otherHsaAmountsFetchKey: null
};

export const useHsaTotalStore = defineStore("hsaTotal", {
    state: (): HsaTotalState => (cloneDeep(emptyState)),
    getters: {
        // Get loaded will return if all the dependent data is loaded
        // This means
        // * the userpayrollsummary
        // * the predicted contributions
        // * I think we might need the bank pending too but i don't have that yet
        isLoaded () {
            return this.userPayrollSummaryIsLoaded;
        },
        userPayrollSummaryIsLoaded () {
            return this.userPayrollFetchState === FetchState.Loaded;
        },
        // When the user edits the annual contribution on the in RecurringContribution component
        // they can't edit the amount lower than this mimimumRecurringAnnualContribution.
        // The mimimum annual contribution should contain all deposits for the year
        // that have already "locked" as well as the current boost amount if there is a boost
        mimimumRecurringAnnualContribution () {
            if (!this.isLoaded) {
                return 0;
            }

            return this.payrollSummary.totalPastRecurring
                + this.payrollSummary.totalOneTimeAmount;
        },

        // this is the legal limit less the employer amount less the amount for prior hsa
        maximumRecurringAnnualContribution () {
            if (!this.userPayrollSummaryIsLoaded) {
                return 0;
            }

            const hsaLimitStore = useHsaLimitStore();
            const hsaStore = useHsaStore();

            return ((hsaLimitStore.getHsaLimitByYear() || 0) -
                this.payrollSummary.employerAmount -
                this.otherHsaAmount() -
                (hsaStore.totalEmployeePersonalContributions() || 0) -
                (hsaStore.employeePostTaxContributions() || 0));
        },

        initialAnnualPreTaxContribution () {
            if (!this.userPayrollSummaryIsLoaded) {
                return 0;
            }

            return floor(this.mimimumRecurringAnnualContribution +
                this.payrollSummary.totalUpcomingRecurring, 2);
        },

        recurringPreTaxContribution (): number {
            if (!this.isLoaded || !this.userPayrollSummaryIsLoaded) {
                return 0;
            }
            return floor(this.payrollSummary.totalPastRecurring +
                this.payrollSummary.totalUpcomingRecurring, 2);
        },

        // based on whatever payroll date can be editted, return the year of that payroll date
        activePayrollYear () {
            if (this.isLoaded &&
                this.payrollSummary.nextEligiblePayDate) {
                return this.payrollSummary.nextEligiblePayDate.year();
            }

            return moment().year();
        },

        // Return all the contributions the user (excluding employer) has made for the active payroll year
        yearToDateTotalPersonalContributions () {
            if (!this.userPayrollSummaryIsLoaded) {
                return 0;
            }
        },
        maximumRemainingContributionYear () {
            return (year: number) => {
                const hsaLimitStore = useHsaLimitStore();
                const hsaStore = useHsaStore();

                return Math.max((hsaLimitStore.getHsaLimitByYear(year) || 0)
                    - (this.otherHsaAmount(year) || 0)
                    - (hsaStore.totalEmployeePersonalContributions(year) || 0)
                    - (hsaStore.totalEmployerPayrollContributions(year) || 0)
                    - (hsaStore.employeePreTaxContributions(year) || 0)
                    - (hsaStore.employeePostTaxContributions(year) || 0)
                    - (hsaStore.bankDirectContributions(year) || 0)
                    , 0);
            };
        },
        maximumRemainingContribution () {
            if (this.allowPayrollContributions === false) {
                // const hsaLimitStore = useHsaLimitStore();
                // const hsaStore = useHsaStore();
                const year = moment().year();
                return this.maximumRemainingContributionYear(year);

                // return Math.max((hsaLimitStore.getHsaLimitByYear(year) || 0)
                //     - (this.otherHsaAmount(year) || 0)
                //     - (hsaStore.totalEmployeePersonalContributions(year) || 0)
                //     - (hsaStore.totalEmployerPayrollContributions(year) || 0)
                //     - (hsaStore.employeePreTaxContributions(year) || 0)
                //     - (hsaStore.employeePostTaxContributions(year) || 0)
                //     - (hsaStore.bankDirectContributions(year) || 0)
                //     , 0);
            }
            return floor((this.maximumRecurringAnnualContribution -
                this.mimimumRecurringAnnualContribution), 2);
        },
        employerAmount () {
            return this.payrollSummary.employerAmount;
        },
        nextEligiblePayDate: state => state.payrollSummary.nextEligiblePayDate,
        nextPayDate: state => state.payrollSummary.nextPayDate,
        nextEligiblePayPeriodAmount: state => state.payrollSummary.nextEligiblePayPeriodAmount,
        eligibilityStatus: state => state.payrollSummary.eligibilityStatus,
        remainingPayPeriods: state => state.payrollSummary.remainingPayPeriods,
        oneTimeLift: state => state.payrollSummary.oneTimeLift,
        totalOnetime: state => state.payrollSummary.totalOneTimeAmount,
        totalPastRecurring: state => state.payrollSummary.totalPastRecurring,
        totalUpcomingRecurring: state => state.payrollSummary.totalUpcomingRecurring,
        totalUpcomingOneTime: state => state.payrollSummary.totalUpcomingOneTime,
        otherHsaAmount () {
            return (year?: number) => {
                if (!year) {
                    year = moment().year();
                }
                if (this.otherHsaAmountsLoaded && this.otherHsaAmountsByYear) {
                    const otherHsa = this.otherHsaAmountsByYear[year];
                    if (otherHsa) {
                        return otherHsa;
                    }
                }
                return 0;
            };
        },
        nextYearEmployerTotal: state => {
            return state.payrollSummary.nextYearEmployerTotal;
        },
        nextYearEmployeeTotal: state => {
            return state.payrollSummary.nextYearEmployeeTotal;
        },
        priorYearPayrollContributionTotal: state => {
            return state.payrollSummary.priorYearTotalPayrollAmount || 0;
        },
        getterNextYear () {
            if (this.nextYear) {
                return this.nextYear + 1;
            }

            return this.activePayrollYear + 1;
        },
        personalScheduledContributionsByYear: state => (year: number, includeProjected: boolean) => {
            if (isEmpty(state.payrollSummary.personalScheduledContributionsByYear)) {
                return 0;
            }

            let contributions = state.payrollSummary.personalScheduledContributionsByYear[year];

            if (!contributions) {
                return 0;
            }

            if (!includeProjected) {
                contributions = contributions.filter(contribution => !contribution.isProjected);
            }

            return contributions
                .map(contribution => contribution.amount)
                .reduce((accumulator, amount) => accumulator + amount, 0);
        },
        nextPayrollFunding: state => state.payrollSummary.nextPayrollFunding,
        otherHsaAmountsLoaded () {
            return this.otherHsaAmountsFetchState === FetchState.Loaded;
        }
    },
    actions: {
        setEarlyDepositCompleteDate (newValue) {
            if (!newValue) {
                this.earlyDepositCompleteDate = null;
            } else {
                this.earlyDepositCompleteDate = moment(newValue);
            }
        },
        setPayrollSummary ({ fetchKey, value }: { fetchKey: FetchStateKey, value: PayrollSummary }) {
            if (!fetchStateKeyMatch(this.fetchKey, fetchKey)) {
                return;
            }
            this.userPayrollFetchState = FetchState.Loaded;
            const existingPersonalScheduledContributions = this.payrollSummary.personalScheduledContributionsByYear;
            this.payrollSummary = {
                ...value,
                nextEligiblePayDate: moment(value.nextEligiblePayDate),
                nextPayDate: moment(value.nextPayDate),
                oneTimeLift: {
                    ...value.oneTimeLift,
                    payDate: value.oneTimeLift ? moment(value.oneTimeLift.payDate) : moment(value.nextEligiblePayDate)
                },
                personalScheduledContributionsByYear: existingPersonalScheduledContributions
            };
        },
        setPayrollSummaryPersonalScheduledContributions (
            response: PayrollSummaryPersonalScheduledContributions) {
            this.payrollSummary.personalScheduledContributionsByYear = response.personalScheduledContributionsByYear;
        },
        setFetching (key: FetchStateKey) {
            this.userPayrollFetchState = FetchState.Loading;
            this.fetchKey = key;
        },
        setFetchLoaded () {
            this.userPayrollFetchState = FetchState.Loaded;
        },
        setShowProjectedVsActualLinks (showProjectedVsActualLinks: boolean) {
            this.showProjectedVsActualLinks = showProjectedVsActualLinks;
        },
        setNextYear (nextYear?: number) {
            this.nextYear = nextYear;
        },
        setAllowPayrollContributions (allowPayrollContributions: boolean) {
            this.allowPayrollContributions = allowPayrollContributions;
        },
        setAlwaysUseInitialAnnualContributionForTotalEmployeePayrollContributions (newValue: boolean) {
            this.alwaysUseInitialAnnualContributionForTotalEmployeePayrollContributions = newValue;
        },
        invalidateState (key: FetchStateKey) {
            if (fetchStateKeyMatch(key, this.fetchKey)) {
                return;
            }
            const stateClone = cloneDeep(emptyState);
            for (const key in stateClone) {
                this[key] = stateClone[key];
            }
        },
        updateRecurringContribution (amount: number): Promise<any> {
            const apiArgs = {
                amount,
                date: this.payrollSummary.nextEligiblePayDate.format("YYYY-MM-DD")
            };

            return payrollService.updateRecurringEmployeeContribution(apiArgs)
                .then(response => {
                    const data = response.data;
                    const newEarlyCompleteDate = apiArgs.amount > 0.01 ? data.earlyDepositCompleteDate : null;
                    this.setEarlyDepositCompleteDate(newEarlyCompleteDate);

                    let errMsg = "";
                    switch (data.resultCode) {
                        case PayPeriodUpdateResultCode.UnrecognizedPayDate:
                            errMsg = "The paydate submitted was not recognized";
                            break;
                        case PayPeriodUpdateResultCode.MissedDeadline:
                            errMsg = "Could not update as the deadline has passed";
                            break;
                        case PayPeriodUpdateResultCode.OverLimit:
                            errMsg = "Could not update as it would put you over the contribution limit for this year.";
                            break;
                        case PayPeriodUpdateResultCode.OneTimeAmountAutoDecreasedToLimit:
                            errMsg
                                = "Could not update as the lift contribution would put you over the contribution limit for this year.";
                            break;
                        case PayPeriodUpdateResultCode.IneligiblePayDate:
                            errMsg = "Could not update as there was one or more ineligible pay dates.";
                            break;
                        case PayPeriodUpdateResultCode.None:
                            break;
                        default:
                            errMsg = "Sorry, your contribution amount could not be updated. Please try again later.";
                            break;
                    }

                    if (errMsg) {
                        const notifierStore = useNotifierStore();
                        notifierStore.setDanger(errMsg);
                    }
                })
                .then(data => {
                    return this.reload();
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        reload (forceRefetch = false) {
            let username = null;
            let partnerId = null;
            const fetchKey = this.fetchKey;
            if (fetchKey) {
                username = this.fetchKey.username;
                partnerId = this.fetchKey.partnerId;
            }

            if (forceRefetch) {
                this.setFetching(fetchKey);
            }
            const hsaDailyBalanceStore = useHsaDailyBalanceStore();
            hsaDailyBalanceStore.reloadPredictedDailyBalances();
            mappedUserPayrollSummary(this, username, partnerId)
                .then(response => this.setPayrollSummary({ fetchKey, value: response.data }))
                .catch(err => {
                    if (this.allowPayrollContributions === true) {
                        storeHelper.handleError({ error: err, rethrow: true });
                    }
                });
        },
        getUserPayrollSummary (args?: {
            username?: string,
            partnerId?: number
        }) {
            args = args || {};

            const fetchKey: FetchStateKey = {
                username: args.username,
                partnerId: args.partnerId
            };

            this.invalidateState(fetchKey);

            if (this.userPayrollFetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            this.setFetching(fetchKey);
            this.setShowProjectedVsActualLinks(true);
            this.setNextYear(null);
            let allowPayroll = true;

            const userStore = useUserStore();
            if (userStore.employeeFundingOption !== FundingOption.BendCalculatePull) {
                allowPayroll = false;
            }
            this.setAllowPayrollContributions(allowPayroll);
            this.setAlwaysUseInitialAnnualContributionForTotalEmployeePayrollContributions(false);

            return mappedUserPayrollSummary(this, args.username, args.partnerId)
                .then(response => this.setPayrollSummary({ fetchKey, value: response.data }))
                .catch(err => {
                    // 404 returned if user is not found, does not have a payroll payrollSummary
                    // set state to loaded to prevent repeated 404 requests
                    if (errorIsStatus(err, 404)) {
                        this.setShowProjectedVsActualLinks(false);
                        this.setNextYear(moment().year());
                        this.setAllowPayrollContributions(false);
                        this.setAlwaysUseInitialAnnualContributionForTotalEmployeePayrollContributions(true);
                        this.setFetchLoaded();
                    } else {
                        storeHelper.handleError({ error: err, rethrow: false });
                    }
                });
        },
        updateOneTimeLiftContribution (amount: number): Promise<any> {
            const payDate = moment(this.payrollSummary.nextPayDate).format("YYYY-MM-DD");
            return payrollService.updateUserLiftContribution(payDate, amount)
                .then(response => response.data)
                .then(data => {
                    const notifierStore = useNotifierStore();
                    if (data.resultCode === PayPeriodUpdateResultCode.UnrecognizedPayDate) {
                        notifierStore.setDanger("The paydate submitted was not recognized");
                    } else if (data.resultCode === PayPeriodUpdateResultCode.MissedDeadline) {
                        notifierStore.setDanger("Could not update as the deadline has passed");
                    } else if (data.resultCode === PayPeriodUpdateResultCode.OverLimit) {
                        notifierStore.setDanger("The update amount is over the limit what you can contribute");
                    }
                })
                .then(data => {
                    return this.reload();
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        updateOtherHsa (data: { amount: number, year: number }): Promise<any> {
            const otherHsa = { ...data };
            return UserService.saveOtherHsaAmount(otherHsa).then(response => response.data)
                .then(() => {
                    const notifierStore = useNotifierStore();
                    notifierStore.setSuccess("External HSA contribution amount has been updated");
                    this.otherHsaAmountsByYear = { ...this.otherHsaAmountsByYear, [data.year]: data.amount };
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        loadOtherHsaAmounts (args?: {
            username?: string,
            partnerId?: number
            force?: boolean } ): Promise<any> {
            args = args || {};

            const fetchKey: FetchStateKey = {
                username: args.username,
                partnerId: args.partnerId
            };

            if (!fetchStateKeyMatch(this.otherHsaAmountsFetchKey, fetchKey)) {
                this.otherHsaAmountsFetchState = FetchState.Uninitialized;
            }

            if (fetchStateKeyMatch(this.otherHsaAmountsFetchKey, fetchKey)
                && this.otherHsaAmountsFetchState !== FetchState.Uninitialized
                && !args.force) {
                return Promise.resolve();
            }

            this.otherHsaAmountsFetchKey = fetchKey;
            if (!args.force
                || this.otherHsaAmountsFetchState !== FetchState.Loaded) {
                this.otherHsaAmountsFetchState = FetchState.Loading;
            }

            return UserService.getOtherHsaAmounts(args.username, args.partnerId)
                .then(response => {
                    this.otherHsaAmountsByYear = response.data;
                    this.otherHsaAmountsFetchState = FetchState.Loaded;
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        }
    }
});